import { ArrowPathIcon, CheckIcon, CheckCircleIcon, ClockIcon, XCircleIcon } from "@heroicons/react/24/outline";
import CircularProgress from "@mui/material/CircularProgress";
import { EConsortiumCgpStatus } from "common/enums/Consortium/EConsortiumCgpStatus";
import { ESubFilesTypes } from "common/enums/Subscription/File/ESubFilesTypes";
import ConsortiumPageResponseResource from "common/resources/Consortium/ConsortiumPageResponseResource";
import ConsortiumRegistrationRequestResource from "common/resources/Consortium/ConsortiumRegistrationRequestResource";
import ConsortiumRegistrationResponseResource from "common/resources/Consortium/ConsortiumRegistrationResponseResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import FileUploaderComponent, { EFileTypes } from "components/elements/FileUploaderComponent";
import LineInformation from "components/elements/LineInformation";
import MessageBox from "components/elements/MessageBox";
import Typography, { ITypo, ITypoColor } from "components/elements/Typography";
import Form from "components/materials/Form";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import MuiSelectInputElement, { IMuiSelectOption } from "components/materials/Form/MuiSelectInputElement";
import I18n from "components/materials/I18n";
import PageTemplate from "components/PageTemplate";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ConsortiumService from "services/ConsortiumService";
import { container } from "tsyringe";

import classes from "./classes.module.scss";

const consortiumService = container.resolve(ConsortiumService);
const ACCEPTED_FILE_TYPES = [EFileTypes.PDF, EFileTypes.JPEG, EFileTypes.PNG, EFileTypes.JPG];
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

type DocumentType = "orias" | "rcp";
type RegistrationFiles = Partial<Record<DocumentType, File>>;
type PreviewScenario = "real" | "form_with_mobile" | "form_without_mobile" | EConsortiumCgpStatus | "origin_cf";

export default function Consortium() {
	const copy = I18n.asset.pages.consortium;
	const [page, setPage] = useState<ConsortiumPageResponseResource | null>(null);
	const [files, setFiles] = useState<RegistrationFiles>({});
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [loadError, setLoadError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [previewScenario, setPreviewScenario] = useState<PreviewScenario>("real");
	const displayedRegistration = useMemo(() => (page ? getPreviewRegistration(page.registration, previewScenario) : null), [page, previewScenario]);
	const changePreviewScenario = useCallback((scenario: PreviewScenario) => {
		setErrors([]);
		setFiles({});
		setPreviewScenario(scenario);
	}, []);

	const loadPage = useCallback(async () => {
		setIsLoading(true);
		setLoadError(false);
		try {
			setPage(await consortiumService.getPage());
		} catch {
			setLoadError(true);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadPage();
	}, [loadPage]);

	const selectFiles = useCallback((type: DocumentType, selectedFiles: File[]) => {
		setFiles((currentFiles) => {
			const nextFiles = { ...currentFiles };
			if (selectedFiles[0]) nextFiles[type] = selectedFiles[0];
			else delete nextFiles[type];
			return nextFiles;
		});
	}, []);

	const submit = useCallback(
		async (_event: FormEvent<HTMLFormElement>, formData: { [key: string]: unknown }) => {
			const request = ConsortiumRegistrationRequestResource.hydrate<ConsortiumRegistrationRequestResource>({
				mobile: String(formData["mobile"] ?? "").trim(),
			});

			setErrors([]);
			setIsSubmitting(true);
			try {
				await request.validateOrReject();
				if (IS_DEVELOPMENT && isFormPreview(previewScenario)) {
					setPreviewScenario(EConsortiumCgpStatus.en_attente);
					setFiles({});
					toast.success(copy.preview.submit_success);
					return;
				}
				setPage(await consortiumService.subscribe(request, files));
				setPreviewScenario("real");
				setFiles({});
			} catch (error) {
				if (Array.isArray(error)) setErrors(error as ValidationError[]);
			} finally {
				setIsSubmitting(false);
			}
		},
		[copy.preview.submit_success, files, previewScenario],
	);

	const refresh = useCallback(async () => {
		setErrors([]);
		setIsRefreshing(true);
		try {
			if (IS_DEVELOPMENT && previewScenario !== "real") {
				await new Promise((resolve) => window.setTimeout(resolve, 1200));
				toast.success(copy.status.refresh_success);
				return;
			}
			setPage(await consortiumService.refresh());
			toast.success(copy.status.refresh_success);
		} catch (error) {
			if (Array.isArray(error)) setErrors(error as ValidationError[]);
		} finally {
			setIsRefreshing(false);
		}
	}, [copy.status.refresh_success, previewScenario]);

	return (
		<PageTemplate tabTitle={copy.page_title} headerTitle={copy.page_title}>
			<div className={classes["root"]}>
				<PageIntroduction />
				{IS_DEVELOPMENT && page && <PreviewControl scenario={previewScenario} onChange={changePreviewScenario} />}

				{isLoading && (
					<div className={classes["loading"]}>
						<CircularProgress size={28} />
						<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.common.loading}</Typography>
					</div>
				)}

				{!isLoading && loadError && (
					<div className={classes["load-error"]}>
						<MessageBox type="warning" text={copy.load_error} />
						<Button onClick={() => void loadPage()}>{copy.retry}</Button>
					</div>
				)}

				{!isLoading && page && !displayedRegistration && (
					<div className={classes["layout"]}>
						<RegistrationForm
							key={previewScenario}
							page={page}
							mobile={getPreviewMobile(page.profile.mobile, previewScenario)}
							errors={errors}
							isSubmitting={isSubmitting}
							onFilesSelected={selectFiles}
							onSubmit={submit}
						/>
						<ProcessSummary />
					</div>
				)}

				{!isLoading && displayedRegistration && (
					<div className={classes["layout"]}>
						<RegistrationStatus registration={displayedRegistration} errors={errors} isRefreshing={isRefreshing} onRefresh={() => void refresh()} />
						<ProcessSummary status={displayedRegistration.status} />
					</div>
				)}
			</div>
		</PageTemplate>
	);
}

function PageIntroduction() {
	const copy = I18n.asset.pages.consortium;
	return (
		<div className={classes["introduction"]}>
			<Typography typo={ITypo.H2}>{copy.title}</Typography>
			<Typography typo={ITypo.P_MEDIUM} color={ITypoColor.WILD_SAND_700}>
				{copy.description}
			</Typography>
		</div>
	);
}

type RegistrationFormProps = {
	page: ConsortiumPageResponseResource;
	mobile: string | null;
	errors: ValidationError[];
	isSubmitting: boolean;
	onFilesSelected: (type: DocumentType, files: File[]) => void;
	onSubmit: (event: FormEvent<HTMLFormElement>, formData: { [key: string]: unknown }) => void;
};

function RegistrationForm({ page, mobile, errors, isSubmitting, onFilesSelected, onSubmit }: RegistrationFormProps) {
	const copy = I18n.asset.pages.consortium;
	const profile = page.profile;
	const isMobileMissing = !mobile?.trim();
	return (
		<section className={classes["main-section"]}>
			<div className={classes["section-heading"]}>
				<Typography typo={ITypo.H2}>{copy.form.title}</Typography>
				<Typography typo={ITypo.P_MEDIUM} color={ITypoColor.WILD_SAND_700}>
					{copy.form.description}
				</Typography>
			</div>

			<div className={classes["profile-summary"]}>
				<LineInformation label={copy.form.advisor} value={`${profile.firstName} ${profile.lastName}`} />
				<LineInformation label={copy.form.email} value={profile.email} />
				<LineInformation label={copy.form.cabinet} value={profile.cabinet} />
				<LineInformation label={copy.form.orias_number} value={profile.numeroOrias} />
			</div>

			<Form className={classes["form"]} errors={errors} onSubmit={onSubmit}>
				<div className={classes["mobile-field"]}>
					<InputElement
						label={copy.form.mobile}
						name="mobile"
						type={EInputType.TEXT}
						defaultValue={mobile}
						placeholder="06 12 34 56 78"
						isNumericString={{ allowSymbols: true }}
						caption={isMobileMissing ? copy.form.mobile_missing_caption : copy.form.mobile_caption}
					/>
				</div>

				<div className={classes["submit-section"]}>
					<Typography typo={ITypo.P_SMALL} color={ITypoColor.WILD_SAND_700}>
						{copy.form.consent}
					</Typography>
					<Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
						{copy.form.submit}
					</Button>
				</div>

				<div className={classes["documents-section"]}>
					<div className={classes["documents-heading"]}>
						<Typography typo={ITypo.P_LARGE_BOLD}>{copy.documents.title}</Typography>
						<Typography typo={ITypo.P_SMALL} color={ITypoColor.WILD_SAND_700}>
							{copy.documents.description}
						</Typography>
					</div>
					<div className={classes["documents"]}>
						<FileUploaderComponent
							title={copy.documents.orias}
							fileType={ESubFilesTypes.OTHERS}
							filesAccepted={ACCEPTED_FILE_TYPES}
							maxFiles={1}
							buttonLabel={copy.documents.add}
							dropLabel={copy.documents.drop}
							onChange={(selectedFiles) => onFilesSelected("orias", selectedFiles)}
						/>
						<FileUploaderComponent
							title={copy.documents.rcp}
							fileType={ESubFilesTypes.OTHERS}
							filesAccepted={ACCEPTED_FILE_TYPES}
							maxFiles={1}
							buttonLabel={copy.documents.add}
							dropLabel={copy.documents.drop}
							onChange={(selectedFiles) => onFilesSelected("rcp", selectedFiles)}
						/>
					</div>
				</div>
			</Form>
		</section>
	);
}

function RegistrationStatus({
	registration,
	errors,
	isRefreshing,
	onRefresh,
}: {
	registration: ConsortiumRegistrationResponseResource;
	errors: ValidationError[];
	isRefreshing: boolean;
	onRefresh: () => void;
}) {
	const copy = I18n.asset.pages.consortium;
	const status = getStatusContent(registration.status);
	return (
		<section className={classes["main-section"]}>
			<div className={classes["status"]} data-status={registration.status}>
				<div className={classes["status-icon"]}>{status.icon}</div>
				<div className={classes["status-copy"]}>
					<Typography className={classes["status-label"]} typo={ITypo.P_SMALL_BOLD}>
						{status.label}
					</Typography>
					<Typography typo={ITypo.H2}>{status.title}</Typography>
					<Typography typo={ITypo.P_MEDIUM}>{registration.originCf ? copy.status.origin_cf : status.text}</Typography>
				</div>
			</div>

			<div className={classes["tracking"]}>
				<Typography typo={ITypo.P_LARGE_BOLD}>{copy.status.tracking_title}</Typography>
				<div>
					<LineInformation label={copy.status.identifier} value={registration.identifiantCgp} />
					<LineInformation label={copy.status.requested_at} value={formatDate(registration.requestedAt)} />
					<LineInformation label={copy.status.updated_at} value={formatDate(registration.statusUpdatedAt, true)} />
					<LineInformation label={copy.status.last_sync} value={formatDate(registration.lastSynchronizedAt, true)} />
				</div>
			</div>

			<Form errors={errors} className={classes["refresh-section"]}>
				<Typography typo={ITypo.P_SMALL} color={ITypoColor.WILD_SAND_700}>
					{copy.status.automatic_sync}
				</Typography>
				<Button variant={EButtonVariant.OUTLINED} icon={<ArrowPathIcon />} loading={isRefreshing} disabled={isRefreshing} onClick={onRefresh}>
					{isRefreshing ? copy.status.refreshing : copy.status.refresh}
				</Button>
			</Form>
		</section>
	);
}

function PreviewControl({ scenario, onChange }: { scenario: PreviewScenario; onChange: (scenario: PreviewScenario) => void }) {
	const copy = I18n.asset.pages.consortium.preview;
	const options: IMuiSelectOption[] = [
		{ id: "real", label: copy.real },
		{ id: "form_with_mobile", label: copy.form_with_mobile },
		{ id: "form_without_mobile", label: copy.form_without_mobile },
		{ id: EConsortiumCgpStatus.en_attente, label: copy.en_attente },
		{ id: EConsortiumCgpStatus.en_cours, label: copy.en_cours },
		{ id: EConsortiumCgpStatus.valide, label: copy.valide },
		{ id: EConsortiumCgpStatus.refuse, label: copy.refuse },
		{ id: "origin_cf", label: copy.origin_cf },
	];

	return (
		<section className={classes["preview"]}>
			<div className={classes["preview-copy"]}>
				<Typography typo={ITypo.P_SMALL_BOLD}>{copy.title}</Typography>
				<Typography typo={ITypo.P_SMALL} color={ITypoColor.WILD_SAND_700}>
					{copy.description}
				</Typography>
			</div>
			<MuiSelectInputElement
				className={classes["preview-select"]}
				name="consortium-preview"
				options={options}
				defaultValue={options.find((option) => option.id === scenario)}
				disableClearable
				onChange={(_event, option) => option && onChange(option.id as PreviewScenario)}
			/>
		</section>
	);
}

function getPreviewRegistration(registration: ConsortiumRegistrationResponseResource | null, scenario: PreviewScenario) {
	if (!IS_DEVELOPMENT || scenario === "real") return registration;
	if (isFormPreview(scenario)) return null;

	const now = new Date();
	return ConsortiumRegistrationResponseResource.hydrate<ConsortiumRegistrationResponseResource>({
		identifiantCgp: registration?.identifiantCgp ?? "CGP-WEN-RECETTE",
		status: scenario === "origin_cf" ? EConsortiumCgpStatus.valide : scenario,
		originCf: scenario === "origin_cf",
		requestedAt: registration?.requestedAt ?? now,
		statusUpdatedAt: now,
		lastSynchronizedAt: now,
	});
}

function getPreviewMobile(mobile: string | null, scenario: PreviewScenario) {
	if (!IS_DEVELOPMENT || scenario === "real") return mobile;
	if (scenario === "form_without_mobile") return null;
	if (scenario === "form_with_mobile") return mobile?.trim() ? mobile : "0612345678";
	return mobile;
}

function isFormPreview(scenario: PreviewScenario): scenario is "form_with_mobile" | "form_without_mobile" {
	return scenario === "form_with_mobile" || scenario === "form_without_mobile";
}

function ProcessSummary({ status }: { status?: EConsortiumCgpStatus }) {
	const copy = I18n.asset.pages.consortium;
	const activeStep = status === EConsortiumCgpStatus.valide ? 3 : status ? 1 : 0;
	const steps = [copy.steps.request, copy.steps.contact, copy.steps.activation];

	return (
		<aside className={classes["process"]}>
			<Typography typo={ITypo.H2}>{copy.process_title}</Typography>
			<div className={classes["steps"]}>
				{steps.map((step, index) => {
					const state =
						status === EConsortiumCgpStatus.refuse && index === activeStep ? "refused" : index < activeStep ? "done" : index === activeStep ? "active" : "upcoming";
					return (
						<div className={classes["step"]} data-state={state} key={step.title} aria-current={state === "active" ? "step" : undefined}>
							<div className={classes["step-marker"]}>{state === "done" ? <CheckIcon /> : index + 1}</div>
							<div>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{step.title}</Typography>
								<Typography typo={ITypo.P_SMALL} color={ITypoColor.WILD_SAND_700}>
									{step.text}
								</Typography>
							</div>
						</div>
					);
				})}
			</div>
			{!status && <MessageBox type="info" text={copy.documents.later} />}
		</aside>
	);
}

function getStatusContent(status: EConsortiumCgpStatus) {
	const copy = I18n.asset.pages.consortium.status;
	switch (status) {
		case EConsortiumCgpStatus.en_attente:
			return { ...copy.en_attente, icon: <ClockIcon /> };
		case EConsortiumCgpStatus.en_cours:
			return { ...copy.en_cours, icon: <ArrowPathIcon /> };
		case EConsortiumCgpStatus.valide:
			return { ...copy.valide, icon: <CheckCircleIcon /> };
		case EConsortiumCgpStatus.refuse:
			return { ...copy.refuse, icon: <XCircleIcon /> };
	}
}

function formatDate(value: Date, withTime = false) {
	return new Intl.DateTimeFormat("fr-FR", {
		dateStyle: "long",
		...(withTime ? { timeStyle: "short" as const } : {}),
	}).format(new Date(value));
}
